import { supabase } from '@/lib/supabase'
import type { Enums } from '@db/database.types'
import type { Cadence, ReviewHistory } from '@core/rules/reviewCadence.js'

/**
 * Everything the team member portal tabs read and write.
 *
 * Kept apart from queries.ts because these are the writing screens. A read that
 * comes back empty here is as likely to be row level security as an empty
 * table, and the review policies are deliberately narrower than the rest of the
 * schema, so the failure messages say so rather than leaving a manager
 * wondering why a colleague can see something they cannot.
 */

function fail(context: string, error: { message: string } | null): never {
  throw new Error(`${context}: ${error?.message ?? 'unknown error'}`)
}

/* ── supervisions and appraisals ──────────────────────────── */

export type ReviewKindAll = 'supervision' | 'appraisal' | 'probation_review'
export type ReviewMeetingStatus = 'scheduled' | 'held' | 'missed' | 'cancelled'

export interface ReviewMeeting {
  id: string
  kind: ReviewKindAll
  status: ReviewMeetingStatus
  scheduledFor: string | null
  heldOn: string | null
  conductedByName: string | null
  summary: string | null
  actionsAgreed: string | null
  concernsRaised: boolean
  outcome: string | null
  createdAt: string
}

export interface ReviewTabData {
  meetings: ReviewMeeting[]
  cadence: Cadence | null
  history: ReviewHistory | null
}

export async function loadReviews(
  organisationId: string,
  employmentId: string,
): Promise<ReviewTabData> {
  const [meetings, cadence] = await Promise.all([
    supabase
      .from('review_meetings')
      .select(
        `id, kind, status, scheduled_for, held_on, conducted_by_name, summary,
         actions_agreed, concerns_raised, outcome, created_at`,
      )
      .eq('organisation_id', organisationId)
      .eq('employment_id', employmentId)
      .order('held_on', { ascending: false, nullsFirst: true })
      .order('scheduled_for', { ascending: false }),
    supabase
      .from('review_cadence_resolved')
      .select(
        `start_date, probation_end_date, supervision_interval_months,
         appraisal_interval_months, probation_supervision_interval_months,
         grace_days, last_supervision_on, last_appraisal_on`,
      )
      .eq('organisation_id', organisationId)
      .eq('employment_id', employmentId)
      .maybeSingle(),
  ])

  if (meetings.error) {
    fail(
      'Could not load supervisions and appraisals. These are readable by HR and by ' +
        'managers of the relevant department only',
      meetings.error,
    )
  }

  const c = cadence.data
  return {
    meetings: meetings.data.map((m) => ({
      id: m.id,
      kind: m.kind as ReviewKindAll,
      status: m.status as ReviewMeetingStatus,
      scheduledFor: m.scheduled_for,
      heldOn: m.held_on,
      conductedByName: m.conducted_by_name,
      summary: m.summary,
      actionsAgreed: m.actions_agreed,
      concernsRaised: m.concerns_raised,
      outcome: m.outcome,
      createdAt: m.created_at,
    })),
    cadence:
      c === null || c === undefined
        ? null
        : {
            supervisionIntervalMonths: c.supervision_interval_months ?? 0,
            appraisalIntervalMonths: c.appraisal_interval_months ?? 0,
            probationSupervisionIntervalMonths: c.probation_supervision_interval_months,
            graceDays: c.grace_days ?? 14,
          },
    history:
      c === null || c === undefined || c.start_date === null
        ? null
        : {
            startDate: c.start_date,
            probationEndDate: c.probation_end_date,
            lastSupervisionOn: c.last_supervision_on,
            lastAppraisalOn: c.last_appraisal_on,
          },
  }
}

export interface LogReviewInput {
  organisationId: string
  employmentId: string
  personId: string
  departmentId: string | null
  kind: ReviewKindAll
  heldOn: string
  conductedByName: string
  summary: string
  actionsAgreed: string
  outcome: string
  concernsRaised: boolean
}

export async function logReview(input: LogReviewInput): Promise<void> {
  const { data: session } = await supabase.auth.getUser()
  const { error } = await supabase.from('review_meetings').insert({
    organisation_id: input.organisationId,
    employment_id: input.employmentId,
    person_id: input.personId,
    department_id: input.departmentId,
    kind: input.kind,
    status: 'held',
    held_on: input.heldOn,
    conducted_by_user_id: session.user?.id ?? null,
    conducted_by_name: input.conductedByName.trim() === '' ? null : input.conductedByName.trim(),
    summary: input.summary.trim() === '' ? null : input.summary.trim(),
    actions_agreed: input.actionsAgreed.trim() === '' ? null : input.actionsAgreed.trim(),
    outcome: input.outcome.trim() === '' ? null : input.outcome.trim(),
    concerns_raised: input.concernsRaised,
    created_by_user_id: session.user?.id ?? null,
  })
  if (error) fail('Could not save this meeting', error)
}

/* ── pay ──────────────────────────────────────────────────── */

export interface PayRate {
  id: string
  effectiveFrom: string
  effectiveTo: string | null
  hourlyRate: number | null
  annualSalary: number | null
  currency: string
  reason: string | null
  source: string
  createdAt: string
}

export async function loadPayHistory(
  organisationId: string,
  employmentId: string,
): Promise<PayRate[]> {
  const { data, error } = await supabase
    .from('pay_rates')
    .select(
      `id, effective_from, effective_to, hourly_rate, annual_salary, currency,
       reason, source, created_at`,
    )
    .eq('organisation_id', organisationId)
    .eq('employment_id', employmentId)
    .order('effective_from', { ascending: false })
  if (error) fail('Could not load pay history', error)

  return data.map((r) => ({
    id: r.id,
    effectiveFrom: r.effective_from,
    effectiveTo: r.effective_to,
    hourlyRate: r.hourly_rate === null ? null : Number(r.hourly_rate),
    annualSalary: r.annual_salary === null ? null : Number(r.annual_salary),
    currency: r.currency,
    reason: r.reason,
    source: r.source,
    createdAt: r.created_at,
  }))
}

export interface AddPayRateInput {
  organisationId: string
  employmentId: string
  effectiveFrom: string
  hourlyRate: number | null
  annualSalary: number | null
  reason: string
  /** The row currently in force, so it can be closed off rather than overlapped. */
  supersedesId: string | null
}

export async function addPayRate(input: AddPayRateInput): Promise<void> {
  const { data: session } = await supabase.auth.getUser()

  // Close the previous row the day before the new one starts. Two open-ended
  // rates would leave "what are they paid today" with two answers.
  if (input.supersedesId !== null) {
    const dayBefore = new Date(`${input.effectiveFrom}T00:00:00Z`)
    dayBefore.setUTCDate(dayBefore.getUTCDate() - 1)
    const { error } = await supabase
      .from('pay_rates')
      .update({ effective_to: dayBefore.toISOString().slice(0, 10) })
      .eq('id', input.supersedesId)
    if (error) fail('Could not close off the previous rate', error)
  }

  const { error } = await supabase.from('pay_rates').insert({
    organisation_id: input.organisationId,
    employment_id: input.employmentId,
    effective_from: input.effectiveFrom,
    hourly_rate: input.hourlyRate,
    annual_salary: input.annualSalary,
    reason: input.reason.trim() === '' ? null : input.reason.trim(),
    source: 'manual',
    changed_by_user_id: session.user?.id ?? null,
  })
  if (error) fail('Could not save the new rate', error)
}

/* ── contracted hours ─────────────────────────────────────── */

export interface ContractVersion {
  id: string
  version: number
  effectiveFrom: string
  effectiveTo: string | null
  contractType: string
  payBasis: string
  contractedHoursPerWeek: number | null
  isIrregularHours: boolean
  noticePeriodDays: number | null
  createdAt: string
}

export interface QueuedWrite {
  outboxId: number
  status: string
  runAfter: string
  attempts: number
  lastError: string | null
  processedAt: string | null
  hours: number | null
  effectiveFrom: string | null
}

export interface HoursTabData {
  versions: ContractVersion[]
  queue: QueuedWrite[]
}

export async function loadContractHistory(
  organisationId: string,
  employmentId: string,
): Promise<HoursTabData> {
  const [contracts, queue] = await Promise.all([
    supabase
      .from('contracts')
      .select(
        `id, version, effective_from, effective_to, contract_type, pay_basis,
         contracted_hours_per_week, is_irregular_hours, notice_period_days, created_at`,
      )
      .eq('organisation_id', organisationId)
      .eq('employment_id', employmentId)
      .order('version', { ascending: false }),
    supabase
      .from('planday_write_queue')
      .select('outbox_id, status, run_after, attempts, last_error, processed_at, payload')
      .eq('organisation_id', organisationId)
      .eq('employment_id', employmentId)
      .eq('entity_type', 'employee_contracted_hours')
      .order('created_at', { ascending: false })
      .limit(10),
  ])

  if (contracts.error) fail('Could not load contract history', contracts.error)

  return {
    versions: contracts.data.map((c) => ({
      id: c.id,
      version: c.version,
      effectiveFrom: c.effective_from,
      effectiveTo: c.effective_to,
      contractType: c.contract_type,
      payBasis: c.pay_basis,
      contractedHoursPerWeek:
        c.contracted_hours_per_week === null ? null : Number(c.contracted_hours_per_week),
      isIrregularHours: c.is_irregular_hours,
      noticePeriodDays: c.notice_period_days,
      createdAt: c.created_at,
    })),
    // A queue that cannot be read is not an error worth blocking the tab for.
    queue:
      queue.error !== null || queue.data === null
        ? []
        : queue.data.map((q) => {
            const p = (q.payload ?? {}) as Record<string, unknown>
            return {
              outboxId: q.outbox_id as number,
              status: q.status as string,
              runAfter: q.run_after as string,
              attempts: q.attempts as number,
              lastError: q.last_error,
              processedAt: q.processed_at,
              hours:
                typeof p.contractedHoursPerWeek === 'number' ? p.contractedHoursPerWeek : null,
              effectiveFrom: typeof p.effectiveFrom === 'string' ? p.effectiveFrom : null,
            }
          }),
  }
}

export type ContractType = Enums<'contract_type'>
export type PayBasis = Enums<'pay_basis'>

export interface AddContractInput {
  organisationId: string
  employmentId: string
  previous: ContractVersion | null
  effectiveFrom: string
  contractType: ContractType
  payBasis: PayBasis
  contractedHoursPerWeek: number | null
  noticePeriodDays: number | null
}

export async function addContractVersion(input: AddContractInput): Promise<void> {
  const nextVersion = (input.previous?.version ?? 0) + 1

  if (input.previous !== null) {
    const dayBefore = new Date(`${input.effectiveFrom}T00:00:00Z`)
    dayBefore.setUTCDate(dayBefore.getUTCDate() - 1)
    const { error } = await supabase
      .from('contracts')
      .update({ effective_to: dayBefore.toISOString().slice(0, 10) })
      .eq('id', input.previous.id)
    if (error) fail('Could not close off the previous contract version', error)
  }

  // The Planday write is queued by a database trigger, not from here, so a
  // change made by any route reaches Planday on its effective date.
  const { error } = await supabase.from('contracts').insert({
    organisation_id: input.organisationId,
    employment_id: input.employmentId,
    version: nextVersion,
    effective_from: input.effectiveFrom,
    contract_type: input.contractType,
    pay_basis: input.payBasis,
    contracted_hours_per_week: input.contractedHoursPerWeek,
    notice_period_days: input.noticePeriodDays,
  })
  if (error) fail('Could not save the new contract version', error)
}

/* ── equipment ────────────────────────────────────────────── */

export type EquipmentCondition =
  | 'as_new'
  | 'good'
  | 'fair'
  | 'damaged'
  | 'lost'
  | 'not_returned'

export interface EquipmentItem {
  id: string
  item: string
  category: string | null
  serialNumber: string | null
  issuedOn: string
  dueBackOn: string | null
  returnedOn: string | null
  conditionOnReturn: EquipmentCondition | null
  replacementValue: number | null
  currency: string
  acknowledgedOn: string | null
  notes: string | null
}

export async function loadEquipment(
  organisationId: string,
  employmentId: string,
): Promise<EquipmentItem[]> {
  const { data, error } = await supabase
    .from('equipment_issues')
    .select(
      `id, item, category, serial_number, issued_on, due_back_on, returned_on,
       condition_on_return, replacement_value, currency, acknowledged_on, notes`,
    )
    .eq('organisation_id', organisationId)
    .eq('employment_id', employmentId)
    .order('issued_on', { ascending: false })
  if (error) fail('Could not load equipment', error)

  return data.map((q) => ({
    id: q.id,
    item: q.item,
    category: q.category,
    serialNumber: q.serial_number,
    issuedOn: q.issued_on,
    dueBackOn: q.due_back_on,
    returnedOn: q.returned_on,
    conditionOnReturn: q.condition_on_return as EquipmentCondition | null,
    replacementValue: q.replacement_value === null ? null : Number(q.replacement_value),
    currency: q.currency,
    acknowledgedOn: q.acknowledged_on,
    notes: q.notes,
  }))
}

export interface IssueEquipmentInput {
  organisationId: string
  employmentId: string
  personId: string
  item: string
  category: string
  serialNumber: string
  issuedOn: string
  dueBackOn: string | null
  replacementValue: number | null
  acknowledgedOn: string | null
  notes: string
}

export async function issueEquipment(input: IssueEquipmentInput): Promise<void> {
  const { data: session } = await supabase.auth.getUser()
  const blank = (s: string) => (s.trim() === '' ? null : s.trim())

  const { error } = await supabase.from('equipment_issues').insert({
    organisation_id: input.organisationId,
    employment_id: input.employmentId,
    person_id: input.personId,
    item: input.item.trim(),
    category: blank(input.category),
    serial_number: blank(input.serialNumber),
    issued_on: input.issuedOn,
    due_back_on: input.dueBackOn,
    replacement_value: input.replacementValue,
    acknowledged_on: input.acknowledgedOn,
    notes: blank(input.notes),
    issued_by_user_id: session.user?.id ?? null,
  })
  if (error) fail('Could not record this item', error)
}

export async function returnEquipment(
  id: string,
  returnedOn: string,
  condition: EquipmentCondition,
): Promise<void> {
  const { data: session } = await supabase.auth.getUser()
  const { error } = await supabase
    .from('equipment_issues')
    .update({
      returned_on: returnedOn,
      condition_on_return: condition,
      received_by_user_id: session.user?.id ?? null,
    })
    .eq('id', id)
  if (error) fail('Could not record the return', error)
}

/* ── tab badges ───────────────────────────────────────────── */

export interface PortalBadges {
  equipmentOut: number
  equipmentOverdue: number
  reviewsRecorded: number
  concernsRaised: number
}

/**
 * Small counts for the tab strip. Deliberately separate from the tab queries:
 * a badge that costs a full tab load is a badge that stops being shown.
 */
export async function loadPortalBadges(
  organisationId: string,
  employmentId: string,
  now: string,
): Promise<PortalBadges> {
  const [equipment, reviews] = await Promise.all([
    supabase
      .from('equipment_issues')
      .select('due_back_on')
      .eq('organisation_id', organisationId)
      .eq('employment_id', employmentId)
      .is('returned_on', null),
    supabase
      .from('review_meetings')
      .select('concerns_raised')
      .eq('organisation_id', organisationId)
      .eq('employment_id', employmentId)
      .eq('status', 'held'),
  ])

  const out = equipment.data ?? []
  const held = reviews.data ?? []

  return {
    equipmentOut: out.length,
    equipmentOverdue: out.filter((q) => q.due_back_on !== null && q.due_back_on < now).length,
    reviewsRecorded: held.length,
    concernsRaised: held.filter((r) => r.concerns_raised).length,
  }
}
